import { computed, onMounted } from 'vue'
import { useSettingsStore } from '@/stores/settings'

export type Theme = 'light' | 'dark' | 'system'

/**
 * Composable for theme management
 * Delegates to the settings store for state management and persistence.
 *
 * Uses cookies for cross-subdomain persistence with Astro landing page.
 */
export function useTheme() {
  const settingsStore = useSettingsStore()

  const isDark = computed(() => settingsStore.resolvedTheme === 'dark')

  /**
   * Set the theme preference and persist it
   */
  const setTheme = (newTheme: Theme): void => {
    // Don't sync to API on theme toggle (handled separately for authenticated users)
    settingsStore.setTheme(newTheme, false)
  }

  /**
   * Toggle between light and dark themes
   * Note: This sets an explicit preference, not 'system'
   */
  const toggleTheme = (): void => {
    const newTheme = settingsStore.resolvedTheme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
  }

  /**
   * Force update theme from external source (e.g., API settings)
   * Used when API settings are loaded after login
   */
  const forceSetTheme = (newTheme: Theme): void => {
    settingsStore.setTheme(newTheme, false)
  }

  // Ensure settings store is initialized when composable is used
  onMounted(() => {
    if (!settingsStore.initialized) {
      settingsStore.initializeFromStorage()
    }
  })

  return {
    theme: computed(() => settingsStore.theme),
    resolvedTheme: settingsStore.resolvedTheme,
    isDark,
    setTheme,
    toggleTheme,
    forceSetTheme,
  }
}
